/**
 * AI Readiness Bar Component
 * Displays user's progress towards AI readiness with visual feedback
 */

import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { READINESS_LEVELS, ReadinessLevel } from '../../types/gamification';
import { HintsPanel } from './HintsPanel';

interface AIReadinessBarProps {
  totalXP: number;
  readinessPercentage: number;
  completedTasks: string[];
  nextTask?: {
    id: string;
    title: string;
    description: string;
    xpReward: number;
  } | null;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  disableCollapse?: boolean;
  variant?: 'full' | 'widget';
}

export const AIReadinessBar: React.FC<AIReadinessBarProps> = ({
  totalXP,
  readinessPercentage,
  completedTasks,
  nextTask,
  isCollapsed = false,
  onToggleCollapse,
  disableCollapse = false,
  variant = 'full',
}) => {
  const navigate = useNavigate();
  const [animatedPercentage, setAnimatedPercentage] = useState(0);
  const [currentLevel, setCurrentLevel] = useState<ReadinessLevel>(READINESS_LEVELS[0]);

  // Animate percentage on mount and when it changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedPercentage(readinessPercentage);
    }, 100);
    return () => clearTimeout(timer);
  }, [readinessPercentage]);

  // Update current level based on percentage
  useEffect(() => {
    const level =
      READINESS_LEVELS.find(
        (l) => readinessPercentage >= l.min && readinessPercentage <= l.max
      ) || READINESS_LEVELS[0];
    setCurrentLevel(level);
  }, [readinessPercentage]);

  if (!disableCollapse && isCollapsed && variant === 'full') {
    return (
      <div className="fixed top-16 left-0 right-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center space-x-4">
              <span className="text-2xl">{currentLevel.emoji}</span>
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900">
                  AI Readiness: {readinessPercentage.toFixed(1)}%
                </div>
              </div>
            </div>
            {!disableCollapse && (
              <button
                onClick={onToggleCollapse}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Expand readiness bar"
              >
                <ChevronDown className="w-5 h-5 text-gray-600" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'widget') {
    return (
      <div className="w-[320px] bg-white rounded-xl shadow-xl border border-neutral-200 overflow-hidden">
        <div className="p-3 space-y-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xl">{currentLevel.emoji}</span>
              <div>
                <h3 className="text-sm font-bold text-gray-900">
                  AI Readiness: {readinessPercentage.toFixed(1)}%
                </h3>
                <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">
                  {currentLevel.label} • {totalXP} XP
                </p>
              </div>
            </div>
            {!disableCollapse && onToggleCollapse && (
              <button
                onClick={onToggleCollapse}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close readiness details"
              >
                <ChevronUp className="w-4 h-4 text-gray-500" />
              </button>
            )}
          </div>

          {/* Progress Bar */}
          <div className="space-y-1">
            <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden shadow-inner">
              <div
                className={`absolute inset-y-0 left-0 bg-gradient-to-r ${currentLevel.color} transition-all duration-1000 ease-out rounded-full`}
                style={{ width: `${animatedPercentage}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 animate-pulse"></div>
              </div>
            </div>
            <div className="flex justify-between items-center text-[10px] text-gray-500">
              <span>0%</span>
              <span>Goal: 100%</span>
            </div>
          </div>

          {/* Next Step / Hints */}
          <div className="pt-2 border-t border-gray-100">
            <HintsPanel
              userGamification={{
                totalXP,
                readinessPercentage,
                completedTasks,
              }}
              nextTask={nextTask}
              compact={true}
            />
          </div>

          <button
            onClick={() => navigate('/ai-readiness')}
            className="w-full flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-primary hover:bg-primary/5 rounded-lg transition-colors"
          >
            View Full Progress
            <ExternalLink className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <div className="flex items-start justify-between">
          {/* Progress Bar Section - Left 50% */}
          <div className="flex-1 pr-6">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center space-x-2">
                <span className="text-2xl">{currentLevel.emoji}</span>
                <div>
                  <h3 className="text-base font-bold text-gray-900">
                    AI Readiness: {readinessPercentage.toFixed(1)}%
                  </h3>
                  <p className="text-xs text-gray-600">
                    {currentLevel.label} • {totalXP} XP
                  </p>
                </div>
              </div>
              {!disableCollapse && (
                <button
                  onClick={onToggleCollapse}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                  aria-label="Collapse readiness bar"
                >
                  <ChevronUp className="w-5 h-5 text-gray-600" />
                </button>
              )}
            </div>

            {/* Progress Bar */}
            <div className="relative h-6 bg-gray-200 rounded-full overflow-hidden shadow-inner">
              <div
                className={`absolute inset-y-0 left-0 bg-gradient-to-r ${currentLevel.color} transition-all duration-1000 ease-out rounded-full`}
                style={{ width: `${animatedPercentage}%` }}
              >
                {/* Shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 animate-pulse"></div>
              </div>

              {/* Percentage label inside bar */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-bold text-white drop-shadow-md">
                  {readinessPercentage.toFixed(1)}%
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-gray-500">
                Your goal: Reach 100% to unlock full AI confidence
              </p>
              <button
                onClick={() => navigate('/ai-readiness')}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 transition-colors"
              >
                View All Tasks
                <ExternalLink className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Hints & Tips Section - Right 50% */}
          <div className="flex-1 pl-4 border-l border-gray-200">
            <HintsPanel
              userGamification={{
                totalXP,
                readinessPercentage,
                completedTasks,
              }}
              nextTask={nextTask}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIReadinessBar;
